import type { ActiveStreamSession } from '@/stores/visionStore'
import { chatRouteId } from '@/lib/isHomeRoute'

/** Per-chat stream session (multi-stream: not only `activeStreamSession`). */
export function streamSessionForChatId(
  chatId: string | null | undefined,
  activeStreamSession: ActiveStreamSession | null,
  activeStreams: Record<string, ActiveStreamSession>,
): ActiveStreamSession | null {
  if (!chatId) return null
  return (
    activeStreams[chatId] ||
    (activeStreamSession?.chatId === chatId ? activeStreamSession : null)
  )
}

/** Whether the viewed chat has a parked, live, or aborted stream slot. */
export function chatHasTrackedStream(
  chatId: string | null | undefined,
  streamingChatIds: string[],
  activeStreamSession: ActiveStreamSession | null,
  activeStreams: Record<string, ActiveStreamSession>,
): boolean {
  if (!chatId) return true
  if (streamingChatIds.includes(chatId)) return true
  if (activeStreams[chatId]) return true
  if (activeStreamSession?.chatId === chatId) return true
  return false
}

/** Banner when the open chat is not the one still streaming or aborted. */
export function shouldShowParkedStreamBanner(opts: {
  sessionChatId?: string | null
  viewedChatId?: string | null
  isStreaming?: boolean
  isAborted?: boolean
  isInspectingHistory?: boolean
  /** Same chat, answer panel closed with ✕ — keep the header banner. */
  evidenceHidden?: boolean
  /** Page reload restored an in-progress or aborted stream on this chat. */
  restoredOnLoad?: boolean
  /** User resumed this chat's existing answer (not a fresh question). */
  isStreamResume?: boolean
  /** A closed banner was brought back by a new question or Retry. */
  showForUserAction?: boolean
  /** Navigated to another route/page away from the active stream's chat. */
  isOtherPage?: boolean
  /** Stream finished while the user was on another chat or page. */
  isCompleted?: boolean
}): boolean {
  if (!opts.isStreaming && !opts.isAborted && !opts.isCompleted) return false
  if (opts.isOtherPage) return true
  if (
    opts.sessionChatId &&
    opts.viewedChatId &&
    opts.sessionChatId === opts.viewedChatId
  ) {
    return false
  }
  if (
    opts.sessionChatId &&
    opts.viewedChatId &&
    opts.sessionChatId !== opts.viewedChatId
  ) {
    return true
  }
  if (opts.sessionChatId && !opts.viewedChatId) {
    return true
  }
  return Boolean(opts.isInspectingHistory)
}

/** Home resets the canvas but must keep a live, aborted, or completed banner. */
export function homeResetKeepsStreamNotice(
  session: {
    isStreaming?: boolean
    isAborted?: boolean
    isCompleted?: boolean
  } | null,
  isStreaming: boolean,
): boolean {
  return Boolean(
    (session &&
      (session.isStreaming || session.isAborted || session.isCompleted)) ||
      isStreaming,
  )
}

/** How long a finished stream banner stays after the user has left that chat. */
export const COMPLETED_STREAM_NOTICE_MS = 60_000

/** Milliseconds left before a completed-away banner should clear. Null when it has no clock. */
export function completedNoticeRemainingMs(
  completedAt: number | undefined,
  now = Date.now(),
): number | null {
  if (!completedAt) return null
  return completedAt + COMPLETED_STREAM_NOTICE_MS - now
}

/**
 * True when the open route is not this stream's chat.
 * Home, knowledge pages, and a different history chat all count.
 */
export function streamFinishedAwayFromChat(
  streamChatId: string | null | undefined,
  pathname: string,
): boolean {
  if (!streamChatId) return false
  return chatRouteId(pathname) !== streamChatId
}

/** Visible chat/evidence updates belong to the stream only while that chat is open. */
export function shouldPaintLiveStream(opts: {
  streamChatId?: string | null
  viewedChatId?: string | null
  isInspectingHistory?: boolean
}): boolean {
  if (opts.streamChatId && opts.viewedChatId !== opts.streamChatId) {
    return false
  }
  if (opts.isInspectingHistory) return false
  return true
}
